/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import ArticleIcon from "@mui/icons-material/Article";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import throttle from "lodash/throttle";
import { SimpliCityAdminSDK } from "@simplicitycms/simplicity-admin-js-sdk";
import { useAuth } from "cms/auth/hooks/use-auth";
import { Logger } from "cms/utils/log/logger";

const WebpageSearch = (props) => {
    
    const { token } = useAuth();
    const onChooseWebpage = props?.onChooseWebpage;
    const instruction = "Type a navigation title...";

    const [inputValue, setInputValue] = React.useState(null);
    const [options, setOptions] = React.useState([]);
    const [value, setValue] = useState(null);

    const selectWebpage = async function (guid) {
        const webpage = await SimpliCityAdminSDK.webpage.findOne(token, guid);
        setInputValue(webpage.nav_title);
        return webpage;
    };
    
    const fetchData = React.useMemo(() =>
        throttle((request, callback) => {
            const options = { page: 1, pageSize: 100 };
            const input = request.input;
            Logger.debug("input:", input);
            SimpliCityAdminSDK.webpage.search(token, input, options)
                .then((results) => {
                    callback(results);
                    return results;
                });
        }, 200),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [token, inputValue]);
    
    let active = true;
    useEffect(() => {
        if (inputValue === "" || inputValue === null) {
            setOptions(value ? [value] : []);
            return undefined;
        }
        
        fetchData({ input: inputValue }, (searchResults) => {
            if (active) {
                let newOptions = [];
                if (value) {
                    newOptions = [value];
                }
                const search_set = [];
                // const pagination = searchResults?.pagination;
                const results = searchResults?.results;
                Logger.debug("Results: ", results);
                for (const result of results) {
                    search_set.push({
                        id: result.id,
                        guid: result.guid,
                        label: `${result.nav_title} ${result.path}`,
                        // text: result.nav_title,
                        text: `${result.nav_title}  [${result.path}]`,
                    });
                }
                if (results) {
                    newOptions = [...newOptions, ...search_set];
                }
                setOptions(newOptions);
            }
        });
        return () => {
            active = false;
        };
    }, [value, inputValue, fetchData]);
    
    return (
        <>
            <Autocomplete
                id='select-webpage'
                sx={{ width: "100%", backgroundColor: "#FFFFFF" }}
                getOptionLabel={(option) => (typeof option === "string" ? option : option.text)}
                noOptionsText={instruction}
                filterOptions={(x) => x}
                options={options}
                autoComplete
                includeInputInList
                filterSelectedOptions
                value={value}
                isOptionEqualToValue={(option, value) => {
                    return option.guid === value.guid;
                }}
                onChange={async (event, newValue) => {
                    setOptions(newValue ? [newValue, ...options] : options);
                    if (newValue) {
                        Logger.debug(`selected ${JSON.stringify(newValue)}`);
                        setValue(newValue);
                        const { guid } = newValue || {};
                        const webpage = await selectWebpage(guid);
                        onChooseWebpage(webpage);
                    } else {
                        setValue(null);
                        onChooseWebpage(null);
                    }
                }}
                onInputChange={(event, newInputValue) => {
                    setInputValue(newInputValue);
                }}
                renderInput={(params) => {
                    // Logger.debug(params)
                    return (
                        <TextField
                            {...params}
                            placeholder={"Select or search by navigation title"}
                            fullWidth
                            className='link-selector-create-search'
                        />
                    );
                }}
                renderOption={(props, option) => {
                    const text = option.text;
                    props.key = option.guid;
                    return (
                        <li {...props}>
                            <Grid container alignItems='center'>
                                <Grid item>
                                    <Box component={ArticleIcon} sx={{ color: "text.secondary", mr: 2 }} />
                                </Grid>
                                <Grid item xs>
                                    <Typography className='link-selector-result' variant='body2' color='text.secondary'>
                                        {text}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </li>
                    );
                }}
                fullWidth={true}
            />
        </>
    );
};

export { WebpageSearch };
